import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["text", "phone", "email", "city", "pincode", "number", "textarea", "dropdown", "radio", "checkbox", "date"];
const HAS_OPTIONS = ["dropdown", "radio", "checkbox"];

export default function FormBuilder() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/forms/default").then((r) => setForm(r.data)).catch(() => toast.error("Form not found"));
  }, []);

  if (!form) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  const setField = (i, patch) => {
    const fields = [...form.fields];
    fields[i] = { ...fields[i], ...patch };
    setForm({ ...form, fields });
  };
  const move = (i, dir) => {
    const fields = [...form.fields];
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    [fields[i], fields[j]] = [fields[j], fields[i]];
    fields.forEach((f, idx) => (f.display_order = idx + 1));
    setForm({ ...form, fields });
  };
  const remove = (i) => setForm({ ...form, fields: form.fields.filter((_, idx) => idx !== i) });
  const add = () => setForm({
    ...form,
    fields: [...form.fields, { label: "New Field", field_key: `field_${Date.now()}`, type: "text", required: false, placeholder: "", display_order: form.fields.length + 1, active: true }],
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, form_key: form.form_key, active: form.active !== false,
        fields: form.fields.map((f, idx) => ({
          ...f, display_order: idx + 1,
          options: HAS_OPTIONS.includes(f.type)
            ? (Array.isArray(f.options) ? f.options : String(f.options || "").split(",").map((x) => x.trim()).filter(Boolean))
            : null,
        })),
      };
      await api.post("/forms", payload);
      toast.success("Lead form saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Lead Form Builder</h1>
          <p className="text-muted-foreground text-sm mt-1">Edit the landing-page lead form. Changes reflect instantly on the public page.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-form-button" className="bg-primary text-primary-foreground">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" />Save Form</>}
        </Button>
      </div>

      <div className="space-y-3">
        {form.fields.map((f, i) => (
          <div key={i} data-testid={`form-field-${i}`} className="glass rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-xs text-muted-foreground">#{i + 1} · {f.field_key}</span>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" onClick={() => move(i, -1)} className="h-7 w-7"><ArrowUp className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => move(i, 1)} className="h-7 w-7"><ArrowDown className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(i)} data-testid={`delete-field-${i}`} className="h-7 w-7 text-red-400"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <div><Label className="text-xs text-muted-foreground">Label</Label>
                <Input value={f.label} onChange={(e) => setField(i, { label: e.target.value })} className="bg-slate-900/60 border-slate-700 mt-1" /></div>
              <div><Label className="text-xs text-muted-foreground">Field Key</Label>
                <Input value={f.field_key} onChange={(e) => setField(i, { field_key: e.target.value })} className="bg-slate-900/60 border-slate-700 mt-1 font-mono" /></div>
              <div><Label className="text-xs text-muted-foreground">Type</Label>
                <Select value={f.type} onValueChange={(v) => setField(i, { type: v })}>
                  <SelectTrigger className="bg-slate-900/60 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs text-muted-foreground">Placeholder</Label>
                <Input value={f.placeholder || ""} onChange={(e) => setField(i, { placeholder: e.target.value })} className="bg-slate-900/60 border-slate-700 mt-1" /></div>
              {HAS_OPTIONS.includes(f.type) && (
                <div className="md:col-span-2"><Label className="text-xs text-muted-foreground">Options (comma separated)</Label>
                  <Input value={Array.isArray(f.options) ? f.options.join(", ") : (f.options || "")} onChange={(e) => setField(i, { options: e.target.value.split(",").map((x) => x.trim()) })} className="bg-slate-900/60 border-slate-700 mt-1" /></div>
              )}
              <div className="md:col-span-3 grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-muted-foreground">Conditional on field key</Label>
                  <Input value={f.conditional?.field_key || ""} onChange={(e) => setField(i, { conditional: e.target.value ? { ...(f.conditional || {}), field_key: e.target.value, operator: "equals" } : null })} placeholder="e.g. loan_business" className="bg-slate-900/60 border-slate-700 mt-1 font-mono" /></div>
                <div><Label className="text-xs text-muted-foreground">Shows when value equals</Label>
                  <Input value={f.conditional?.value || ""} onChange={(e) => setField(i, { conditional: { ...(f.conditional || { operator: "equals" }), value: e.target.value } })} className="bg-slate-900/60 border-slate-700 mt-1" /></div>
              </div>
            </div>
            <div className="flex items-center gap-6 mt-3">
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.required} onCheckedChange={(v) => setField(i, { required: v })} data-testid={`field-required-${i}`} /> Required</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={f.active !== false} onCheckedChange={(v) => setField(i, { active: v })} data-testid={`field-active-${i}`} /> Active</label>
            </div>
          </div>
        ))}
      </div>

      <Button onClick={add} variant="outline" data-testid="add-field-button" className="border-slate-700 w-full"><Plus className="h-4 w-4 mr-1.5" />Add Field</Button>
    </div>
  );
}
